// src/pages/PlaceOrder.jsx

import React, { useContext, useState, useEffect } from 'react';
import { Box, Grid, Typography, Button, CircularProgress, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import CartTotal from '../components/CartTotal';
import CustomerDetailsForm from '../components/CustomerDetailsForm';
import { ShopContext } from '../context/ShopContext';

const PlaceOrder = () => {
  const [method, setMethod] = useState('cod');
  const [customerDetails, setCustomerDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const { placeOrder } = useContext(ShopContext);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      try {
        const response = await api.get('/api/users/profile');
        if (response.data) {
          setCustomerDetails({
            firstName: response.data.firstName,
            lastName: response.data.lastName,
            email: response.data.email,
            address: response.data.address,
            city: response.data.city,
            state: response.data.state,
            zipCode: response.data.zipCode,
            country: response.data.country,
            phone: response.data.phone,
          });
          setShowForm(false);
        } else {
          setShowForm(true);
        }
      } catch (err) {
        if (err.response?.status === 401) {
          navigate('/login', { state: { from: '/place-order' } });
        } else {
          console.error('Error fetching customer details:', err);
          setShowForm(true);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerDetails();
  }, [navigate]);

  const handleDetailsSaved = (details) => {
    setCustomerDetails(details);
    setShowForm(false);
  };

  const handlePlaceOrder = async () => {
    if (!customerDetails) {
      setError('Please fill in your delivery information');
      return;
    }
    
    try {
      setIsPlacingOrder(true);
      setError(null);
      
      // Call the placeOrder function from ShopContext
      await placeOrder();
      
      // The placeOrder function will handle the navigation to /orders on success
    } catch (error) {
      console.error('Error placing order:', error);
      setError(error.response?.data?.message || 'Failed to place order. Please try again.');
    } finally {
      setIsPlacingOrder(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={4}>
        {/* Left Side: Delivery Information */}
        <Grid item xs={12} md={6}>
          <Typography variant="h5" component="h2" fontWeight="bold" gutterBottom>
            Delivery Information
          </Typography>
          
          {showForm ? (
            <CustomerDetailsForm 
              onComplete={handleDetailsSaved} 
              isCheckout={true} 
            />
          ) : (
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {customerDetails?.firstName} {customerDetails?.lastName}
              </Typography>
              <Typography>{customerDetails?.address}</Typography>
              <Typography>
                {customerDetails?.city}, {customerDetails?.state} {customerDetails?.zipCode}
              </Typography>
              <Typography>{customerDetails?.country}</Typography>
              <Typography>Phone: {customerDetails?.phone}</Typography>
              <Typography>Email: {customerDetails?.email}</Typography>
              
              <Button 
                variant="outlined" 
                onClick={() => setShowForm(true)}
                sx={{ mt: 2 }}
              >
                Edit Details
              </Button>
            </Paper>
          )}
        </Grid>

        {/* Right Side: Payment Method and Cart Total */}
        <Grid item xs={12} md={6}>
          <CartTotal />
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" component="h2" fontWeight="bold" gutterBottom>
              Payment Method
            </Typography>
            <Grid container spacing={2} alignItems="center" sx={{ mt: 2 }}>
              <Grid item>
                <Box
                  onClick={() => setMethod('cod')}
                  sx={{
                    p: 1,
                    border: '2px solid',
                    borderColor: method === 'cod' ? 'green' : 'grey.300',
                    borderRadius: 2,
                    cursor: 'pointer',
                    textAlign: 'center',
                    minWidth: 120,
                  }}
                >
                  <Typography variant="body2">Cash On Delivery</Typography>
                </Box>
              </Grid>
              <Grid item>
                <Box
                  onClick={() => setMethod('stripe')}
                  sx={{
                    p: 1,
                    border: '2px solid',
                    borderColor: method === 'stripe' ? 'green' : 'grey.300',
                    borderRadius: 2,
                    cursor: 'pointer',
                    textAlign: 'center',
                    minWidth: 120,
                  }}
                >
                  <Typography variant="body2">Stripe</Typography>
                </Box>
              </Grid>
              <Grid item>
                <Box
                  onClick={() => setMethod('razorpay')}
                  sx={{
                    p: 1,
                    border: '2px solid',
                    borderColor: method === 'razorpay' ? 'green' : 'grey.300',
                    borderRadius: 2,
                    cursor: 'pointer',
                    textAlign: 'center',
                    minWidth: 120,
                  }}
                >
                  <Typography variant="body2">Razorpay</Typography>
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ mt: 4 }}>
              {error && (
                <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                  {error}
                </Typography>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handlePlaceOrder}
                disabled={!customerDetails || showForm || isPlacingOrder}
                sx={{
                  textTransform: 'none',
                  fontWeight: 'bold',
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: 3,
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.02)',
                  },
                  '&:disabled': {
                    bgcolor: 'action.disabled',
                    color: 'text.disabled',
                  },
                  minWidth: 150,
                }}
              >
                {isPlacingOrder ? (
                  <>
                    <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                    Placing Order...
                  </>
                ) : (
                  'Place Order'
                )}
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PlaceOrder;
